import React, { useState, useEffect } from "react";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { checkPermission } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";
import {
  IconButton,
  Tooltip,
  Box,
} from "@mui/material";
import {
  Edit as EditIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import {
  fetchPersonnel,
  updatePersonnelStatus,
  clearQualificationsCache,
  getPersonnelsWithQualifications,
} from "../../services/personnel/personnel.service";
import AddPersonnelModal from "./AddPersonnelModal";
import "./PersonnelList.css";
import { formatDate } from "utils/formatters";
import { AppButton } from "components/common/buttons";
import { AppTextField } from "components/common/inputs";
import { AppFilterBar } from "components/common/filters";
import DataTable from "components/common/tables/DataTable";

const PersonnelToolbar = ({ onSearch, searchTerm, onAdd }) => {
  const [searchInput, setSearchInput] = useState(searchTerm || "");

  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onSearch(searchInput);
  };

  return (
    <AppFilterBar icon={false}>
      <AppTextField
        // type="text"
        placeholder="Tìm kiếm nhân sự (theo tên, chức vụ, phòng ban, trình độ, chuyên ngành, ...) ..."
        value={searchInput}
        onChange={handleSearchChange}
        sx={{ flex: 1 }}
      />
      <AppButton variant="outlined" onClick={handleSearchSubmit}>
        <SearchIcon />
      </AppButton>
      <AppButton
        variant="contained"
        color="primary"
        // style={{ marginLeft: "auto", textTransform: "none", fontWeight: 500 }}
        onClick={onAdd}
        startIcon={<span className="material-icons">add</span>}
        disableElevation
      >
        Thêm nhân sự
      </AppButton>
    </AppFilterBar>
  );
};

const PersonnelList = () => {
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState("displayOrder");
  const [sortDirection, setSortDirection] = useState("asc");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editPersonnelId, setEditPersonnelId] = useState(null);
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("warning");

  // Status Badge Component - tương tự như trong UserList.js
  const PersonnelStatusBadge = ({ isActive }) => {
    return (
      <span
        className="status-badge"
        style={{
          backgroundColor: isActive
            ? "var(--status-completed, #4caf50)"
            : "var(--status-pending, #ff9800)",
        }}
      >
        {isActive ? "Đã kích hoạt" : "Chưa kích hoạt"}
      </span>
    );
  };

  useEffect(() => {
    loadPersonnel();
    // eslint-disable-next-line
  }, [currentPage, pageSize, sortColumn, sortDirection, searchTerm]);

  const loadPersonnel = async () => {
    setLoading(true);
    try {
      const response = await fetchPersonnel(
        pageSize,
        currentPage,
        searchTerm,
        sortColumn,
        sortDirection.toUpperCase()
      );
      if (response && Array.isArray(response.items)) {
        // Load qualifications cho tất cả personnel
        const personnelWithQualifications =
          await getPersonnelsWithQualifications(response.items);
        setPersonnel(personnelWithQualifications);
        setTotalItems(response.totalItems || 0);
      } else {
        setPersonnel([]);
        setTotalItems(0);
      }
    } catch (err) {
      setPersonnel([]);
      setTotalItems(0);
      setSnackbarMessage(err?.response?.data?.message || "Không thể tải danh sách nhân sự hoặc trình độ.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleTablePaginationChange = (updater) => {
    const pagination = { pageIndex: currentPage - 1, pageSize };
    const nextPagination =
      typeof updater === "function" ? updater(pagination) : updater;

    setPageSize(nextPagination.pageSize);
    setCurrentPage(
      nextPagination.pageSize !== pageSize ? 1 : nextPagination.pageIndex + 1
    );
  };

  const handleTableSortingChange = (updater) => {
    const sorting = [{ id: sortColumn, desc: sortDirection === "desc" }];
    const nextSorting = typeof updater === "function" ? updater(sorting) : updater;
    const nextSort = nextSorting[0];

    if (nextSort) {
      setSortColumn(nextSort.id);
      setSortDirection(nextSort.desc ? "desc" : "asc");
      setCurrentPage(1);
    }
  };

  const handleToggleStatus = async (personnel) => {
    const result = await checkPermission(PERMISSIONS.SYSTEM.PERSONNEL_DELETE);
    if (result.hasPermission) {
      try {
        await updatePersonnelStatus(personnel.personnelId, !personnel.isActive);
        // Reload personnel list to show updated status
        loadPersonnel();
      } catch (error) {
        setSnackbarMessage("Không thể cập nhật trạng thái nhân sự. Hãy tải lại để kiểm tra trước khi thử lại.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } else {
      setSnackbarMessage("Bạn không có quyền vô hiệu hóa nhân sự.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
    }
  };

  const handleAddPersonnel = async () => {
    const result = await checkPermission(PERMISSIONS.SYSTEM.PERSONNEL_CREATE);
    if (result.hasPermission) {
      setAddOpen(true);
    } else {
      setSnackbarMessage("Bạn không có quyền thêm nhân sự.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
    }
  };

  const handleAddPersonnelClose = () => {
    setAddOpen(false);
  };

  const handlePersonnelAdded = () => {
    // Xóa cache để đảm bảo dữ liệu mới được tải
    clearQualificationsCache();
    setAddOpen(false);
    setEditOpen(false);
    loadPersonnel();
  };

  const handleEditPersonnel = async (personnel) => {
    const result = await checkPermission(PERMISSIONS.SYSTEM.PERSONNEL_UPDATE);
    if (result.hasPermission) {
      setEditPersonnelId(personnel.personnelId);
      setEditOpen(true);
    } else {
      setSnackbarMessage("Bạn không có quyền chỉnh sửa nhân sự.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
    }
  };

  const handleEditPersonnelClose = () => {
    setEditOpen(false);
    setEditPersonnelId(null);
  };

  const columns = [
    {
      accessorKey: "displayOrder", header: "STT", size: 40,
    },
    {
      accessorKey: "fullName", header: "Họ và tên", size: 140,
    },
    {
      accessorKey: "title", header: "Chức vụ", size: 100,
      Cell: ({ cell }) => cell.getValue() || "N/A",
    },
    {
      id: "qualificationNames", header: "Trình độ", size: 90,
      accessorFn: (row) =>
        row.qualifications?.length
          ? row.qualifications.map((q) => q.qualificationName).join(", ")
          : "Chưa có thông tin",
      enableSorting: false,
    },
    {
      id: "majorNames", header: "Chuyên ngành", size: 120,
      accessorFn: (row) =>
        row.qualifications?.length
          ? row.qualifications.map((q) => q.majorName).filter(Boolean).join(", ") ||
          "Chưa có thông tin"
          : "Chưa có thông tin",
      enableSorting: false,
    },
    {
      accessorKey: "departmentName", header: "Phòng ban", size: 100,
      Cell: ({ cell }) => cell.getValue() || "N/A",
    },
    {
      accessorKey: "enterpriseName", header: "Xí nghiệp", size: 120,
      Cell: ({ cell }) => (
        cell.getValue() || "N/A"
      ),
    },
    {
      accessorKey: "createdAt", header: "Ngày tạo", size: 70,
      Cell: ({ cell }) => (
        formatDate(cell.getValue()) || ""
      ),
    },
    {
      accessorKey: "updatedAt", header: "Ngày cập nhật", size: 100,
      Cell: ({ cell }) => (
        formatDate(cell.getValue()) || ""
      ),
    },
    {
      accessorKey: "isActive", header: "Trạng thái", size: 100,
      Cell: ({ cell }) => {
        return <PersonnelStatusBadge isActive={cell.getValue()} />;
      },
    },
    {
      id: "actions",
      header: "Thao tác",
      size: 60,
      enableSorting: false,
      enableColumnHiding: false,
      Cell: ({ row }) => (
        <Box>
          <Tooltip title="Chỉnh sửa">
            <IconButton size="small" onClick={() => handleEditPersonnel(row.original)} disableElevation>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.original.isActive ? "Vô hiệu hóa" : "Kích hoạt"}>
            <IconButton size="small" color={row.original.isActive ? "error" : "success"} onClick={() => handleToggleStatus(row.original)}>
              {row.original.isActive ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <div className="personnel-container">
      <AddPersonnelModal
        open={addOpen}
        onClose={handleAddPersonnelClose}
        onPersonnelAdded={handlePersonnelAdded}
      />
      <AddPersonnelModal
        open={editOpen}
        onClose={handleEditPersonnelClose}
        onPersonnelAdded={handlePersonnelAdded}
        personnelId={editPersonnelId}
        isEdit={true}
        initialTabIndex={0}
      />
      <div className="fixed-header">
        <PersonnelToolbar
          onSearch={handleSearch}
          searchTerm={searchTerm}
          onAdd={handleAddPersonnel}
        />
      </div>
      <div className="scrollable-content">
        <DataTable
          columns={columns}
          data={personnel}
          enableSorting
          enableSortingRemoval={false}
          manualSorting
          manualPagination
          rowCount={totalItems}
          rowsPerPageOptions={[10, 20, 50, 100]}
          enableGlobalFilter={false}
          getRowId={(row) => String(row.personnelId)}
          onPaginationChange={handleTablePaginationChange}
          onSortingChange={handleTableSortingChange}
          state={{
            isLoading: loading,
            pagination: { pageIndex: currentPage - 1, pageSize },
            sorting: [{ id: sortColumn, desc: sortDirection === "desc" }],
          }}
        />
      </div>

      <AppSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        severity={snackbarSeverity}
        message={snackbarMessage}
      />
    </div >
  );
};

export default PersonnelList;
